export const CUSTOMER_PORTAL_SESSION_KEY = "customerPortalSession";

/** @deprecated legacy flag; cleared on logout */
export const CUSTOMER_PORTAL_AUTH_KEY = "customerPortalAuthenticated";

function readSessionRaw() {
  // Prefer localStorage so email/SMS links in a new tab keep an existing portal session.
  // Fall back to legacy sessionStorage and migrate once found.
  try {
    const fromLocal = localStorage.getItem(CUSTOMER_PORTAL_SESSION_KEY);
    if (fromLocal) return fromLocal;
  } catch {
    /* ignore */
  }
  try {
    const fromSession = sessionStorage.getItem(CUSTOMER_PORTAL_SESSION_KEY);
    if (fromSession) {
      try {
        localStorage.setItem(CUSTOMER_PORTAL_SESSION_KEY, fromSession);
        sessionStorage.removeItem(CUSTOMER_PORTAL_SESSION_KEY);
      } catch {
        /* ignore migrate failures */
      }
      return fromSession;
    }
  } catch {
    /* ignore */
  }
  return null;
}

function writeSessionRaw(value) {
  try {
    localStorage.setItem(CUSTOMER_PORTAL_SESSION_KEY, value);
  } catch {
    /* ignore */
  }
  try {
    sessionStorage.removeItem(CUSTOMER_PORTAL_SESSION_KEY);
  } catch {
    /* ignore */
  }
}

export function initialsFromName(name) {
  if (!name || typeof name !== "string") return "?";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function getCustomerPortalSession() {
  try {
    const raw = readSessionRaw();
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data?.token || Number(data?.user?.role_id) !== 6) return null;
    return data;
  } catch {
    return null;
  }
}

export function setCustomerPortalSession(apiResponse) {
  const nameParts = (apiResponse.full_name || "").trim().split(/\s+/).filter(Boolean);
  const user = {
    id: apiResponse.id,
    full_name: apiResponse.full_name,
    first_name: apiResponse.first_name || nameParts[0] || "",
    last_name: apiResponse.last_name || nameParts.slice(1).join(" ") || "",
    email: apiResponse.email,
    phone_number: apiResponse.phone_number,
    country_code: apiResponse.country_code,
    role_id: apiResponse.role_id,
    salon_id: apiResponse.salon_id,
    profile_photo_url: apiResponse.profile_photo_url,
    customer_portal_settings: apiResponse.customer_portal_settings ?? null,
    cancellation_policy: apiResponse.cancellation_policy ?? null,
  };
  writeSessionRaw(
    JSON.stringify({
      token: apiResponse.token,
      auth_key: apiResponse.auth_key,
      user,
    }),
  );
}

export function updateCustomerPortalUserProfile(profile) {
  const session = getCustomerPortalSession();
  if (!session?.token) return;

  setCustomerPortalSession({
    token: session.token,
    auth_key: session.auth_key,
    id: profile.id ?? session.user.id,
    full_name: profile.full_name ?? session.user.full_name,
    first_name: profile.first_name ?? session.user.first_name,
    last_name: profile.last_name ?? session.user.last_name,
    email: profile.email ?? session.user.email,
    phone_number: profile.phone_number ?? session.user.phone_number,
    country_code: profile.country_code ?? session.user.country_code,
    role_id: profile.role_id ?? session.user.role_id,
    salon_id: profile.salon_id ?? session.user.salon_id,
    profile_photo_url: profile.profile_photo_url ?? session.user.profile_photo_url,
    customer_portal_settings: profile.customer_portal_settings ?? session.user.customer_portal_settings ?? null,
    cancellation_policy: profile.cancellation_policy ?? session.user.cancellation_policy ?? null,
  });
}

export function updateCustomerPortalSettingsInSession(settings) {
  const session = getCustomerPortalSession();
  if (!session?.token) return;

  writeSessionRaw(
    JSON.stringify({
      ...session,
      user: {
        ...session.user,
        customer_portal_settings: settings,
      },
    }),
  );
}

export function clearCustomerPortalSession() {
  try {
    localStorage.removeItem(CUSTOMER_PORTAL_SESSION_KEY);
    localStorage.removeItem(CUSTOMER_PORTAL_AUTH_KEY);
  } catch {
    /* ignore */
  }
  try {
    sessionStorage.removeItem(CUSTOMER_PORTAL_SESSION_KEY);
    sessionStorage.removeItem(CUSTOMER_PORTAL_AUTH_KEY);
  } catch {
    /* ignore */
  }
}

export const PORTAL_ACCOUNT_MISMATCH_MESSAGE =
  "This link is for a different customer. Please sign in with the account used for this booking.";

export function normalizePortalPhone(value) {
  return String(value || "").replace(/\D/g, "");
}

export function normalizeRequiredPortalUser(input) {
  if (!input || typeof input !== "object") {
    return { userId: null, waitlistId: null, salonId: null, phone: null };
  }
  return {
    userId: Number(input.id ?? input.userId ?? input.user_id ?? 0) || null,
    waitlistId: Number(input.waitlistId ?? input.waitlist_id ?? 0) || null,
    salonId: Number(input.salonId ?? input.salon_id ?? 0) || null,
    phone: normalizePortalPhone(input.phone ?? input.phone_number ?? "") || null,
  };
}

export function getRequiredPortalUserFromSearch(search) {
  let query = "";
  if (typeof search === "string") {
    const qIndex = search.indexOf("?");
    query = qIndex >= 0 ? search.slice(qIndex + 1) : search.replace(/^\?/, "");
  }
  const params = new URLSearchParams(query);
  return {
    userId: Number(params.get("user_id") || params.get("client_id") || 0) || null,
    waitlistId: Number(params.get("waitlist_id") || 0) || null,
    salonId: Number(params.get("salon_id") || 0) || null,
    phone: normalizePortalPhone(params.get("phone") || "") || null,
    reauth: params.get("reauth") === "1",
  };
}

export function getRequiredPortalUserFromLocation(location) {
  const from = location?.state?.from;
  const fromSearch = typeof from === "string" ? from : typeof from?.search === "string" ? from.search : "";
  const fromPathname = typeof from === "string" ? from.split("?")[0] : from?.pathname || "";
  const fromFull = typeof from === "string" ? from : `${fromPathname}${fromSearch}`;
  const own = getRequiredPortalUserFromSearch(location?.search);
  const nested = getRequiredPortalUserFromSearch(fromFull);
  const stateRequired = normalizeRequiredPortalUser(location?.state?.requiredUser);

  return {
    userId: own.userId || nested.userId || stateRequired.userId,
    waitlistId: own.waitlistId || nested.waitlistId || stateRequired.waitlistId,
    salonId: own.salonId || nested.salonId || stateRequired.salonId,
    phone: own.phone || nested.phone || stateRequired.phone,
    reauth: own.reauth || nested.reauth || Boolean(location?.state?.forceLogin),
  };
}

export function hasRequiredPortalUser(required) {
  return Boolean(required?.userId || required?.phone);
}

export function sessionMatchesRequiredPortalUser(session, required) {
  if (!hasRequiredPortalUser(required)) {
    return true;
  }
  if (!session?.user) {
    return false;
  }
  if (required.userId && Number(session.user.id) !== Number(required.userId)) {
    return false;
  }
  if (required.phone) {
    const sessionPhone = normalizePortalPhone(session.user.phone_number);
    if (sessionPhone && sessionPhone !== required.phone) {
      return false;
    }
  }
  if (required.salonId && Number(session.user.salon_id) !== Number(required.salonId)) {
    return false;
  }
  return true;
}

export function loggedInApiUserMatchesRequired(apiUser, required) {
  if (!hasRequiredPortalUser(required)) {
    return true;
  }
  if (!apiUser) {
    return false;
  }
  if (required.userId && Number(apiUser.id) !== Number(required.userId)) {
    return false;
  }
  if (required.phone) {
    const phone = normalizePortalPhone(apiUser.phone_number);
    if (phone && phone !== required.phone) {
      return false;
    }
  }
  return true;
}

export function verifyAndPreparePortalSession(required) {
  const session = getCustomerPortalSession();
  if (required?.reauth && !hasRequiredPortalUser(required)) {
    if (session) {
      clearCustomerPortalSession();
    }
    return { session: null, matches: false, mismatch: Boolean(session) };
  }
  if (!hasRequiredPortalUser(required)) {
    return { session, matches: true, mismatch: false };
  }
  if (session && sessionMatchesRequiredPortalUser(session, required)) {
    return { session, matches: true, mismatch: false };
  }
  if (session) {
    clearCustomerPortalSession();
    return { session: null, matches: false, mismatch: true };
  }
  return { session: null, matches: false, mismatch: false };
}

/** Best post-login/home path from react-router location.state.from or a fallback. */
export function resolveCustomerPortalReturnPath(from, fallback = "/dashboard") {
  const authPaths = ["/login", "/forgot-password", "/"];
  if (!from) return fallback;
  if (typeof from === "string") {
    const pathOnly = from.split("?")[0].split("#")[0];
    return pathOnly.startsWith("/") && !authPaths.includes(pathOnly) ? from : fallback;
  }
  const pathname = typeof from.pathname === "string" ? from.pathname : "";
  if (!pathname.startsWith("/") || authPaths.includes(pathname)) {
    return fallback;
  }
  const search = typeof from.search === "string" ? from.search : "";
  const hash = typeof from.hash === "string" ? from.hash : "";
  return `${pathname}${search}${hash}`;
}
